import type { ReactNode } from "react"
import { Link } from "react-router-dom"
import { Moon, Sun } from "lucide-react"
import { useTheme } from "../lib/theme"
import { AccountMenu, type AccountMenuUser } from "./AccountMenu"

export interface LayoutProps {
  /** App brand: shown on the left of the header. Click goes to home. */
  brand: { title: string; subtitle?: string; href?: string }
  /** Optional in-app nav links rendered between brand and the account menu. */
  nav?: ReactNode
  /** Central auth service base URL. */
  authBase: string
  /** Current user (null = unauthenticated). */
  user: AccountMenuUser | null
  /** Login URL to use when user is null. If omitted, account menu builds one. */
  loginUrl?: string | null
  /** Caption under the user's name (e.g. "4 tags"). */
  userSubtitle?: string
  /** Footer link back to the parent site. Defaults to lilbillbiscuit.com. */
  footerHref?: string
  /** Body content rendered inside the main container. */
  children: ReactNode
}

/**
 * Standard app shell: sticky header with brand + nav + theme toggle + account
 * menu, content area, simple footer. The app's router renders its own pages
 * inside `children` (or via `<Outlet/>` for nested routes).
 */
export function Layout({
  brand, nav, authBase, user, loginUrl, userSubtitle, footerHref = "https://lilbillbiscuit.com", children,
}: LayoutProps) {
  const { theme, toggle } = useTheme()
  return (
    <div className="min-h-screen flex flex-col">
      <header className="sticky top-0 z-30 border-b border-th-glass/[0.08] bg-th-bg/85 backdrop-blur-xl">
        <div className="mx-auto flex h-14 max-w-7xl items-center justify-between gap-4 px-4">
          <Link to={brand.href ?? "/"} className="flex items-center gap-2">
            <span className="font-heading text-lg font-semibold tracking-tight text-th-fg">
              {brand.title}
            </span>
            {brand.subtitle && (
              <span className="hidden text-xs text-th-fg/40 sm:inline">{brand.subtitle}</span>
            )}
          </Link>
          <div className="flex items-center gap-2 text-sm">
            {nav}
            <button
              onClick={toggle}
              aria-label="Toggle theme"
              className="rounded-lg p-1.5 text-th-fg/60 transition-colors hover:bg-th-glass/[0.06] hover:text-th-fg"
            >
              {theme === "dark" ? <Sun size={15} /> : <Moon size={15} />}
            </button>
            <AccountMenu authBase={authBase} user={user} loginUrl={loginUrl} subtitle={userSubtitle} />
          </div>
        </div>
      </header>
      <main className="mx-auto w-full max-w-7xl flex-1 px-4 py-6">{children}</main>
      <footer className="border-t border-th-glass/[0.08] py-4 text-center text-xs text-th-fg/40">
        part of <a href={footerHref} className="text-th-fg/60 hover:text-th-accent">lilbillbiscuit</a>
      </footer>
    </div>
  )
}
